/**
 * Wave 29 Phase F.1 — JC site-mismatch full-page split-map.
 *
 * When a tech starts a job >200m from the customer's saved coords, the
 * JC banner deep-links here. The page shows:
 *   • Top half: a single map with BOTH the saved customer pin (teal) and
 *     the tech's captured pin (amber), plus a line between them and the
 *     distance label.
 *   • Bottom half: a friendly summary + two big actions: "This is the
 *     correct address → update customer" OR "Tech was at the wrong
 *     place → flag for review (keep customer pin)".
 *
 * Per Wave 28 product rule: this is a FULL PAGE, not a modal — match the
 * pattern used by /jobcards/new, /quotes/new, etc.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ChevronLeft, MapPin, Navigation, Loader2, AlertTriangle,
  CheckCircle2, ShieldAlert,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { createMap, tealPin, L } from "../lib/leafletMap";
import { formatDistance } from "../lib/geo";
import { useAuth } from "../context/AuthContext";

function amberPin(label = "") {
  // Reuses the look of tealPin but in amber so the eye instantly knows
  // "this is the captured location, not the saved one".
  const html = `
    <div style="transform: translate(-50%, -100%); display:flex; flex-direction:column; align-items:center;">
      <div style="
        width:22px; height:22px; border-radius:50%;
        background:#FBBF24; border:2px solid #0a0b0e;
        box-shadow:0 2px 8px rgba(0,0,0,.4);
        display:flex; align-items:center; justify-content:center;
        color:#0a0b0e; font:700 11px/1 system-ui;
      ">${label}</div>
      <div style="
        width:0; height:0;
        border-left:5px solid transparent;
        border-right:5px solid transparent;
        border-top:8px solid #FBBF24;
        margin-top:-1px;
      "></div>
    </div>`;
  return L.divIcon({ className: "hv-pin-amber", html, iconSize: [22, 30], iconAnchor: [11, 30] });
}

export default function JobcardSiteMismatch() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isManager = user && ["owner", "admin", "manager"].includes(user.role);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [address, setAddress] = useState("");
  const mapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/jobcards/${id}/site-mismatch`);
        if (!alive) return;
        setData(data);
        if (data?.customer?.physical_address) setAddress(data.customer.physical_address);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load mismatch data.");
        nav(`/jobcards/${id}`);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id, nav]);

  useEffect(() => {
    if (loading || !data) return;
    const captured = data.captured || {};
    const cust = data.customer || {};
    const hasC = typeof captured.lat === "number" && typeof captured.lng === "number";
    const hasS = typeof cust.lat === "number" && typeof cust.lng === "number";
    if (!hasC && !hasS) return;
    if (!mapRef.current) return;
    // Clean the div in case StrictMode double-mounts.
    mapRef.current.innerHTML = "";
    const center = hasC && hasS
      ? [(captured.lat + cust.lat) / 2, (captured.lng + cust.lng) / 2]
      : (hasC ? [captured.lat, captured.lng] : [cust.lat, cust.lng]);
    const { map } = createMap(mapRef.current, { center, zoom: 13, satelliteFirst: true });
    const layers = [];
    if (hasS) {
      const m = L.marker([cust.lat, cust.lng], { icon: tealPin("S") }).addTo(map);
      m.bindPopup(`<b>Saved customer address</b><br/>${(cust.name || "").replace(/</g, "&lt;")}<br/>${(cust.physical_address || "").replace(/</g, "&lt;")}`);
      layers.push(m);
    }
    if (hasC) {
      const m = L.marker([captured.lat, captured.lng], { icon: amberPin("T") }).addTo(map);
      m.bindPopup(`<b>Tech's captured location</b><br/>Accuracy: ${captured.accuracy_m || "—"} m<br/>Captured: ${captured.captured_at ? new Date(captured.captured_at).toLocaleString() : "—"}`);
      layers.push(m);
    }
    if (hasC && hasS) {
      L.polyline(
        [[cust.lat, cust.lng], [captured.lat, captured.lng]],
        { color: "#FBBF24", weight: 2, dashArray: "6 6", opacity: 0.85 },
      ).addTo(map);
      const group = L.featureGroup(layers);
      map.fitBounds(group.getBounds().pad(0.4));
    }
    return () => { try { map.remove(); } catch (_e) { /* noop */ } };
  }, [data, loading]);

  const updateCustomer = async () => {
    if (!isManager) {
      toast.error("Only managers can update customer locations.");
      return;
    }
    setBusy(true);
    try {
      const body = {};
      if (address && address !== (data?.customer?.physical_address || "")) {
        body.physical_address = address;
      }
      await api.post(`/jobcards/${id}/site-override`, body);
      toast.success("Customer location updated.");
      nav(`/jobcards/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    } finally { setBusy(false); }
  };

  const flagWrongPlace = () => {
    // Soft action — we don't have a separate "flag" DB row yet; just bounce
    // back to the JC and rely on the audit log (hard_mismatch + audit
    // notification). Future: open a discipline / coaching workflow.
    toast.message("Flagged. The mismatch stays recorded on the JC audit log.");
    nav(`/jobcards/${id}`);
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="jc-mismatch-loading">
        <div className="h-6 w-40 bg-white/[0.04] rounded animate-pulse" />
        <div className="h-[55vh] bg-white/[0.04] rounded animate-pulse" />
      </div>
    );
  }
  if (!data) return null;

  const job = data.jobcard || {};
  const cust = data.customer || {};
  const captured = data.captured || {};
  const hasCustCoords = typeof cust.lat === "number" && typeof cust.lng === "number";
  const hasCapturedCoords = typeof captured.lat === "number" && typeof captured.lng === "number";

  return (
    <div className="space-y-4" data-testid="jc-site-mismatch-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link
          to={`/jobcards/${id}`}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="mismatch-back"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back to JC
        </Link>
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 inline-flex items-center gap-1">
          <ShieldAlert className="w-3 h-3" /> Site location mismatch
        </div>
        <span className="w-12" />
      </div>

      <div className="hv-card p-4 sm:p-5">
        <div className="flex items-start gap-3 mb-3 flex-wrap">
          <MapPin className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-xl sm:text-2xl font-bold">
              {job.short_id || "JC"} · {job.title || "Job card"}
            </h1>
            <p className="text-xs text-[#a0aabf] mt-0.5">
              Linked to <Link to={`/customers/${cust.id}`} className="text-[#1FB6C1] underline">{cust.name || "(no customer)"}</Link>
            </p>
            {captured.audit === "hard_mismatch" && (
              <p className="text-sm text-amber-200 mt-2">
                Tech started this job <b>{formatDistance(captured.distance_m)}</b> away from the saved address. That's well outside the normal site radius. <br className="hidden sm:inline" />
                If the tech is at the right place, the saved customer coordinates are wrong — update them below. If the tech is at the wrong place, flag for review.
              </p>
            )}
            {captured.audit === "soft_mismatch" && (
              <p className="text-sm text-amber-200 mt-2">
                Tech started this job <b>{formatDistance(captured.distance_m)}</b> from the saved address. Usually means the saved pin is slightly off — confirm below.
              </p>
            )}
            {captured.audit === "no_saved_location" && (
              <p className="text-sm text-amber-200 mt-2">
                This customer has no saved coordinates yet. Use the tech's captured pin to set the site location once and for all.
              </p>
            )}
            {(captured.audit === "denied" || captured.denied) && (
              <p className="text-sm text-amber-200 mt-2">
                Tech denied location permission when starting this job. The JC is flagged in the audit log; no map data is available.
              </p>
            )}
          </div>
        </div>

        {/* Map */}
        {(hasCustCoords || hasCapturedCoords) && (
          <div
            ref={mapRef}
            className="rounded-lg overflow-hidden border border-white/10"
            style={{ height: "55vh", minHeight: 320 }}
            data-testid="mismatch-map"
          />
        )}

        {/* Legend + summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div className="text-xs border border-[#1FB6C1]/40 rounded p-3 bg-[#1FB6C1]/[0.05]">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#1FB6C1] mb-1">Saved customer address</div>
            <div className="text-[#d7dde8]">{cust.physical_address || "—"}</div>
            <div className="text-[11px] text-[#647087] mt-1">
              {hasCustCoords ? `${cust.lat.toFixed(5)}, ${cust.lng.toFixed(5)}` : "No coords on file"}
            </div>
          </div>
          <div className="text-xs border border-amber-400/40 rounded p-3 bg-amber-400/[0.05]">
            <div className="text-[10px] uppercase tracking-[0.15em] text-amber-300 mb-1">Tech's captured pin</div>
            <div className="text-[#d7dde8]">
              {hasCapturedCoords ? `${captured.lat.toFixed(5)}, ${captured.lng.toFixed(5)}` : "Not captured"}
            </div>
            <div className="text-[11px] text-[#647087] mt-1">
              Accuracy {captured.accuracy_m || "—"} m · {captured.captured_at ? new Date(captured.captured_at).toLocaleString() : "—"}
              {captured.captured_by_name ? ` · by ${captured.captured_by_name}` : ""}
            </div>
          </div>
        </div>

        {/* Optional address rewrite */}
        {isManager && hasCapturedCoords && (
          <div className="mt-4">
            <label htmlFor="mismatch-address" className="block text-[11px] uppercase tracking-[0.15em] text-[#647087] mb-1.5">
              Update street address (optional)
            </label>
            <input
              id="mismatch-address"
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Leave blank to keep the existing address"
              className="hv-input text-sm w-full"
              data-testid="mismatch-address-input"
            />
          </div>
        )}

        {/* Actions */}
        <div className="mt-5 flex flex-wrap items-center gap-2 justify-end border-t border-white/5 pt-4">
          <button
            type="button"
            onClick={flagWrongPlace}
            className="hv-btn hv-btn-ghost text-sm"
            data-testid="mismatch-flag-btn"
          >
            <AlertTriangle className="w-3.5 h-3.5" /> Tech was at the wrong place — keep saved address
          </button>
          {isManager && hasCapturedCoords && (
            <button
              type="button"
              onClick={updateCustomer}
              disabled={busy}
              className="hv-btn hv-btn-primary text-sm disabled:opacity-50"
              data-testid="mismatch-override-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              This is the correct address — update customer
            </button>
          )}
          {!isManager && (
            <span className="text-[11px] text-[#647087] inline-flex items-center gap-1">
              <Navigation className="w-3 h-3" /> Ask a manager to confirm or override the saved pin.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
